import React, { useState, useRef } from "react";
import "./splitter.css";
import { PDFSplitterAPI } from "../../Services/APIService";
import { FileIcon, DownloadIcon } from "lucide-react";
import { ReactComponent as RemoveIcon } from "../../Icons/x.svg";

const SplitPdf = () => {
    const [files, setFiles] = useState([]);
    const [splitMode, setSplitMode] = useState("single");
    const [pageRanges, setPageRanges] = useState("1-1");
    const [pagesPerFile, setPagesPerFile] = useState(1);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const handleDrop = (e) => {
        e.preventDefault();
        handleFileChange(e.dataTransfer.files);
    };
    const handleFileChange = (e) => {
        if (!e.target.files) return;
        setFiles(Array.from(e.target.files));
    };

    const removeFile = (index) => {
        const updated = [...files];
        updated.splice(index, 1);
        setFiles(updated);
    };

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.readAsDataURL(file);

            reader.onload = () => {
                const result = reader.result;
                const base64 = result.split(",")[1];
                resolve(base64);
            };

            reader.onerror = (error) => reject(error);
        });
    };

    const getPayload = async (file) => {
        const base64Pdf = await convertToBase64(file);
        let ranges = [];
        if (splitMode === "single") {
            ranges = ["1-1"];
        }
        if (splitMode === "custom") {
            ranges = pageRanges
                .split(",")
                .map((item) => item.trim())
                .filter(Boolean);
        }
        if (splitMode === "everyN") {
            ranges = [`EVERY_${pagesPerFile}`];
        }
        return {
            base64Pdf,
            pageRanges: ranges,
        };
    };

    const handleSplit = async () => {
        if (!files.length) return;

        try {
            setLoading(true);
            const payload = await getPayload(files[0]);
            const res = await PDFSplitterAPI(payload);
            downloadPdf(res.data.splitBase64Pdfs[0], 0);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const downloadPdf = (base64, index) => {
        const link = document.createElement("a");
        link.href = `data:application/pdf;base64,${base64}`;
        link.download = `split_part_${index + 1}.pdf`;
        link.click();
    };

    return (
        <div className="pdf-merger-container">
            {/* Header */}
            <div className="pdf-header">
                <lable>Split PDF</lable>
                <p>
                    Extract page ranges or split into individual pages
                </p>
            </div>

            {/* Upload Section */}
            <div className="upload-zone"
                onClick={() => inputRef.current.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}>
                <input
                    type="file"
                    accept=".pdf"
                    multiple
                    id="pdf-upload"
                    ref={inputRef}
                    hidden
                    onChange={handleFileChange}
                />

                <div className="upload-icon"><svg
                    className="dmc-upload-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M12 16V4M12 4L7 9M12 4L17 9" />
                    <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                </svg></div>
                <div className="upload-text">
                    Click to select or drop PDF here
                </div>
                <div className="upload-subtext">
                    Files are split in the order shown above
                </div>
            </div>

            {/* Split Modes */}
            <div className="split-mode-box">
                <h2>Split mode</h2>

                <div className="mode-buttons">
                    <button
                        className={
                            splitMode === "single" ? "active" : ""
                        }
                        onClick={() => setSplitMode("single")}
                    >
                        One file per page
                    </button>

                    <button
                        className={
                            splitMode === "custom" ? "active" : ""
                        }
                        onClick={() => setSplitMode("custom")}
                    >
                        Custom Ranges
                    </button>

                    <button
                        className={
                            splitMode === "everyN" ? "active" : ""
                        }
                        onClick={() => setSplitMode("everyN")}
                    >
                        Every N pages
                    </button>
                </div>

                {/* Custom Range */}
                {splitMode === "custom" && (
                    <div className="input-group">
                        <label>Pages</label>
                        <input
                            type="text"
                            value={pageRanges}
                            onChange={(e) =>
                                setPageRanges(e.target.value)
                            }
                            placeholder="1-3,5-8"
                        />
                        <small>
                            Use commas and dashes. Example: 1-3,8-10
                        </small>
                    </div>
                )}

                {/* Every N */}
                {splitMode === "everyN" && (
                    <div className="input-group">
                        <label>Pages per file</label>

                        <input
                            type="number"
                            min={1}
                            value={pagesPerFile}
                            onChange={(e) =>
                                setPagesPerFile(Number(e.target.value))
                            }
                        />
                    </div>
                )}
            </div>

            {/* File List */}
            <div className="file-list">
                {files.map((file, index) => (
                    <div className="splitter-file-card" key={index}>
                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px" }}>
                            <FileIcon />
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <h4>{file.name}</h4>
                                <p>
                                    {(file.size / 1024).toFixed(1)} KB
                                </p>
                            </div>
                        </div>
                        <RemoveIcon className="headerIcon" onClick={() => removeFile(index)} />
                    </div>
                ))}
            </div>

            {/* Split Button */}
            <button
                className="split-btn"
                onClick={handleSplit}
                disabled={loading || !files.length}
            >
                <DownloadIcon className="headerIcon" /> &nbsp;
                {loading ? "Splitting..." : "Split PDF"}
            </button>

        </div>
    );
};

export default SplitPdf;
